import type { RingView } from "../render/decisiveFx3d";
import { cloneMatch, isSlowed, PHASE, SLOW_RATE, type MatchState, type SimEvent } from "../sim";

/**
 * Картинка решающего удара (sim/decisive.ts — замедление в мире): между обработанными кадрами мира — промежуточные
 * (замедление плавное), камера уходит на низкий ракурс 3/4 из-за плеча бьющего и медленно облетает точку удара,
 * попадание — белая вспышка. Только отрисовка: мир решает сам.
 */
/**
 * Уход в ракурс и возврат — ровным ходом с плавным разгоном и замедлением, а не «сразу быстро, потом медленно»
 * (консультант 05.11.2026: «камера с крупного на общий за ~6 кадров — рывок; возврат 0,4–0,6 с»). Шагов мира.
 */
const ENTER_STEPS = 14;
const RETURN_STEPS = 32;
const smoother = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
/** Поворот облёта за шаг мира, рад. */
const ORBIT_TURN = 0.006;

export interface DecisiveFrame {
  readonly state: MatchState;
  /** Кольцо ударной волны: точка попадания в замедлении и сколько кадров картинки прошло; null — не было. */
  readonly ring: RingView | null;
  /** Сила особого ракурса 0..1, угол облёта (рад), чей удар, вспышка 0..1. */
  readonly orbit: number;
  readonly angle: number;
  readonly by: number;
  readonly flash: number;
}

export class DecisiveView {
  private before: MatchState | null = null;
  private last: MatchState | null = null;
  /** Ход ракурса 0..1 по прямой; сила ракурса — его сглаженная доля. */
  private travel = 0;
  private strength = 0;
  private turn = 0;
  private flash = 0;
  /** Попадание в замедлении: где (пиксели мира, y — вверх) и на каком кадре часов картинки. */
  private hit: { x: number; y: number; at: number } | null = null;
  private by = 0;
  /**
   * На сколько кадров часы картинки отстают от мира: в замедлении они идут в SLOW_RATE раз медленнее. Не догоняют —
   * ускоренная стойка или скачок фазы клипа были бы видны; сдвиг сбрасывается на заставке раунда, где модели и так встают
   * на места.
   */
  private lag = 0;
  private lastFrame = -1;

  /**
   * Кадр мира после шага: запомнить обработанные кадры, сдвинуть часы картинки, ракурс и облёт; попадание в
   * замедлении — вспышка. Всё по шагам мира, а не по кадрам экрана: на 120 Гц картинка та же, что на 60.
   */
  record(state: MatchState, events: readonly SimEvent[]): void {
    if (state.frame === this.lastFrame) return; // онлайн: мир ждал соперника и не шагнул
    this.lastFrame = state.frame;
    const active = isSlowed(state);
    if (state.slowTick === 0) {
      this.before = this.last;
      this.last = cloneMatch(state);
    }
    const hit = state.slowmo > 0 ? events.find((event) => event.kind === "hit") : undefined;
    if (hit) {
      this.flash = 1;
      this.hit = { x: hit.x, y: -hit.y, at: state.frame - this.lag };
    }
    if (state.slowmoBy >= 0) this.by = state.slowmoBy;
    if (active) this.lag += 1 - 1 / SLOW_RATE;
    else if (state.phase === PHASE.intro) {
      this.lag = 0;
      this.hit = null;
    }
    this.travel = active ? Math.min(1, this.travel + 1 / ENTER_STEPS) : Math.max(0, this.travel - 1 / RETURN_STEPS);
    this.strength = smoother(this.travel);
    // медленный облёт: ~25° за замедление, ~45° вместе с падением после нокаута
    this.turn += active ? ORBIT_TURN : 0;
  }

  next(state: MatchState): DecisiveFrame | null {
    const active = isSlowed(state);
    if (this.strength < 0.01 && !active && this.lag === 0) {
      this.turn = 0;
      return null;
    }
    const flash = this.flash;
    this.flash = Math.max(0, this.flash - 0.34);
    const shown =
      active && this.before && this.last ? between(this.before, this.last, state.slowTick / SLOW_RATE) : state;
    // часы картинки (переходы клипов, повторы стойки, огонь) — замедленные; живой снимок мира не трогаем
    const display = { ...shown, frame: state.frame - this.lag };
    const ring = this.hit ? { x: this.hit.x, y: this.hit.y, age: display.frame - this.hit.at } : null;
    return { state: display, ring, orbit: this.strength, angle: this.turn, by: this.by, flash };
  }
}

/**
 * Промежуточный кадр между кадрами мира a и b (доля t): место бойцов и кадр их клипа — посередине; если между кадрами
 * сменилось состояние или приём, клип — от a, на долю кадра дальше. Только для отрисовки.
 */
export function between(a: MatchState, b: MatchState, t: number): MatchState {
  const mix = (from: number, to: number): number => from + (to - from) * t;
  const state = cloneMatch(a);
  state.frame = mix(a.frame, b.frame);
  state.fighters.forEach((fighter, side) => {
    const next = b.fighters[side === 0 ? 0 : 1];
    fighter.x = mix(fighter.x, next.x);
    fighter.y = mix(fighter.y, next.y);
    fighter.vy = mix(fighter.vy, next.vy);
    if (fighter.state === next.state && fighter.ability === next.ability) {
      fighter.stateFrame = mix(fighter.stateFrame, next.stateFrame);
      fighter.abilityFrame = mix(fighter.abilityFrame, next.abilityFrame);
    } else {
      // клип сменится на кадре b: до него прежний клип идёт дальше на долю кадра, а не стоит
      fighter.stateFrame += t;
      fighter.abilityFrame += t;
    }
  });
  return state;
}
